import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { PanelLoading } from "../../components/Loading";
import { useTranslation } from "react-i18next";
import {
  gateApi,
  type GateTripView,
  type TripDirection,
  type WeighingKind,
  type WeighingView,
} from "../../api/transport";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import Ic from "../../components/icons.jsx";
import {
  canArrive,
  canDepart,
  firstWeighingKind,
  isWeighingReasonRequired,
  statusTagClass,
  weighingTagClass,
} from "./tripsBoard";

const WEIGHING_KINDS: WeighingKind[] = ["GROSS", "TARE", "CONTROL"];

/**
 * The gate — the narrowest screen in the
 * portal. `PORTIR` holds `trips:gate` and nothing else in this module — not
 * even `trips:view` — so this page talks to `gateApi` only: today's trips,
 * arrive, depart, record a weighing, read a trip's weighings. It never calls
 * `tripsApi` or `carriersApi` the way `TripsPage`/`TripDetailPage` do.
 *
 * `GateTripView` carries `carrierName`/
 * `vehiclePlate`/`trailerPlate`/`driverName` — resolved server-side by
 * `GateService.today` — rather than raw ids, which would render as
 * truncated, meaningless codes. `PORTIR` cannot
 * resolve names itself, and does not need to: the gate route does that
 * resolution before the response ever reaches the browser, so this page
 * still calls nothing beyond `gateApi`. Render `null` the same way every
 * other empty field on this page renders — as "—", never as the literal
 * string "null".
 *
 * No prices, amounts, contracts or catalogue data appears anywhere here —
 * `GateTripView`/`WeighingView` do not carry any, so there is nothing to
 * accidentally render.
 */
export default function GatePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [trips, setTrips] = useState<GateTripView[] | null>(null);

  async function load() {
    try {
      setTrips(await gateApi.today());
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("trips.gate.title", "Gate")}</h1>
          <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
            {t("trips.gate.subtitle", "Today's trips: record arrival, weighings and departure.")}
          </p>
        </div>
      </div>

      {!trips ? (
        <PanelLoading text={t("common.loading")} />
      ) : trips.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("trips.gate.empty", "No trips expected today.")}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {trips.map((trip) => (
            <GateTripCard key={trip.id} trip={trip} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One trip, one card — arrive/depart buttons gated by `canArrive`/`canDepart`
 * (shared with the tests in `tripsBoard.test.ts`), the weighing form, and the
 * weighings recorded so far. Each card loads its own weighings independently
 * so recording one only refreshes that card, not the whole board.
 */
function GateTripCard({ trip, onChanged }: { trip: GateTripView; onChanged: () => Promise<void> }) {
  const { canWrite } = useSectionAccess("transport_gate");
  const { t } = useTranslation();
  const toast = useToast();
  const [weighings, setWeighings] = useState<WeighingView[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadWeighings() {
    try {
      setWeighings(await gateApi.weighings(trip.id));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the trip identity changes
  useEffect(() => { void loadWeighings(); }, [trip.id]);

  async function arrive() {
    setBusy(true);
    try {
      await gateApi.arrive(trip.id);
      toast.success(t("trips.gate.toast.arrived", "Marked as arrived."));
      await onChanged();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function depart() {
    setBusy(true);
    try {
      await gateApi.depart(trip.id);
      toast.success(t("trips.gate.toast.departed", "Marked as departed."));
      await onChanged();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <header className="card-h">
        <div>
          <h3><code>{trip.tripNumber}</code></h3>
          <div className="sub">
            {t(`trips.direction.${trip.direction.toLowerCase()}`)}
            {trip.vehiclePlate ? ` · ${trip.vehiclePlate}` : ""}
          </div>
        </div>
        <span className={`tag ${statusTagClass(trip.status)}`}>
          {t(`trips.status.${trip.status.toLowerCase()}`)}
        </span>
      </header>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={idGrid}>
          <ReadField label={t("trips.col.vehicle", "Vehicle")} value={trip.vehiclePlate ?? "—"} mono />
          <ReadField label={t("trips.field.trailer", "Trailer")} value={trip.trailerPlate ?? "—"} mono />
          <ReadField label={t("trips.col.carrier", "Carrier")} value={trip.carrierName ?? "—"} />
          <ReadField label={t("trips.field.driver", "Driver")} value={trip.driverName ?? "—"} />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          {canWrite && canArrive(trip.status) && (
            <button type="button" className="btn primary" onClick={() => void arrive()} disabled={busy}>
              <Ic.check /> {t("trips.gate.action.arrive", "Arrived")}
            </button>
          )}
          {canWrite && canDepart(trip.status) && (
            <button type="button" className="btn primary" onClick={() => void depart()} disabled={busy}>
              <Ic.arrowRight /> {t("trips.gate.action.depart", "Departed")}
            </button>
          )}
        </div>

        <div style={divider} />

        {canWrite && <WeighingForm
          tripId={trip.id}
          direction={trip.direction}
          onRecorded={async () => {
            await loadWeighings();
            toast.success(t("trips.gate.toast.weighed", "Weighing recorded."));
          }}
        />}

        <div style={divider} />

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h4 style={sectionTitle}>{t("trips.section.weighings", "Weighings")}</h4>
          <WeighingsList weighings={weighings} />
        </div>
      </div>
    </section>
  );
}

/**
 * Kind / weight / scale / ticket number / reason. Reason is required only for
 * a CONTROL weighing (`isWeighingReasonRequired`) — checked client-side so
 * the operator sees it immediately, and left to the server as the final
 * authority (`WEIGHING_REASON_REQUIRED`) if it is ever skipped regardless.
 */
function WeighingForm({
  tripId,
  direction,
  onRecorded,
}: {
  tripId: string;
  direction: TripDirection;
  onRecorded: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<WeighingKind>(firstWeighingKind(direction));
  const [weightKg, setWeightKg] = useState("");
  const [scale, setScale] = useState("");
  const [ticketNo, setTicketNo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reasonRequired = isWeighingReasonRequired(kind);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (reasonRequired && reason.trim() === "") {
      setErr(t("trips.gate.weighing.reasonRequired", "A reason is required for a control weighing."));
      return;
    }
    setBusy(true);
    try {
      await gateApi.weigh(tripId, {
        kind,
        weightKg: Number(weightKg),
        scale: scale.trim() || undefined,
        ticketNo: ticketNo.trim() || undefined,
        reason: reason.trim() || undefined,
      });
      setWeightKg("");
      setScale("");
      setTicketNo("");
      setReason("");
      await onRecorded();
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <h4 style={sectionTitle}>{t("trips.gate.weighing.title", "Record weighing")}</h4>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Field label={t("trips.weighing.col.kind", "Kind")}>
          <Select<string>
            value={kind}
            onChange={(v) => setKind(v as WeighingKind)}
            ariaLabel={t("trips.weighing.col.kind", "Kind")}
            style={{ minWidth: 140 }}
            options={WEIGHING_KINDS.map((k) => ({ value: k, label: t(`trips.weighing.kind.${k.toLowerCase()}`) }))}
          />
        </Field>
        <Field label={t("trips.weighing.col.weight", "Weight (kg)")}>
          <input
            type="number"
            min="0"
            step="0.01"
            required
            className="input"
            style={{ width: 120 }}
            value={weightKg}
            onChange={(e) => setWeightKg(e.target.value)}
          />
        </Field>
        <Field label={t("trips.weighing.col.scale", "Scale")}>
          <input className="input" style={{ width: 120 }} value={scale} onChange={(e) => setScale(e.target.value)} />
        </Field>
        <Field label={t("trips.weighing.col.ticket", "Ticket no.")}>
          <input
            className="input"
            style={{ width: 120 }}
            value={ticketNo}
            onChange={(e) => setTicketNo(e.target.value)}
          />
        </Field>
        <Field label={t("trips.weighing.col.reason", "Reason") + (reasonRequired ? " *" : "")}>
          <input
            className="input"
            style={{ width: 200 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              reasonRequired
                ? t("trips.gate.weighing.reasonPlaceholder", "Required for a control weighing")
                : undefined
            }
          />
        </Field>
        <button type="submit" className="btn primary" disabled={busy || weightKg.trim() === ""}>
          {busy ? "…" : t("trips.gate.weighing.action.record", "Record")}
        </button>
      </div>
    </form>
  );
}

/** Chronological — the server already orders by `measuredAt` ascending
 * (`findByTripIdOrderByMeasuredAtAsc`), so this list is rendered as received. */
function WeighingsList({ weighings }: { weighings: WeighingView[] | null }) {
  const { t, i18n } = useTranslation();

  if (weighings === null) {
    return <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>{t("common.loading")}</p>;
  }
  if (weighings.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>
        {t("trips.weighing.empty", "No weighings recorded yet.")}
      </p>
    );
  }
  return (
    <table className="list-card">
      <thead>
        <tr>
          <th style={th}>{t("trips.weighing.col.kind", "Kind")}</th>
          <th style={th}>{t("trips.weighing.col.weight", "Weight (kg)")}</th>
          <th style={th}>{t("trips.weighing.col.scale", "Scale")}</th>
          <th style={th}>{t("trips.weighing.col.ticket", "Ticket no.")}</th>
          <th style={th}>{t("trips.weighing.col.time", "Time")}</th>
          <th style={th}>{t("trips.weighing.col.reason", "Reason")}</th>
        </tr>
      </thead>
      <tbody>
        {weighings.map((w) => (
          <tr key={w.id}>
            <td style={td} data-label={t("trips.weighing.col.kind", "Kind")}>
              <span className={`tag ${weighingTagClass(w.kind)}`}>
                {t(`trips.weighing.kind.${w.kind.toLowerCase()}`)}
              </span>
            </td>
            <td style={td} data-label={t("trips.weighing.col.weight", "Weight (kg)")}>{w.weightKg}</td>
            <td style={td} data-label={t("trips.weighing.col.scale", "Scale")}>{w.scale ?? "—"}</td>
            <td style={{ ...td, fontFamily: "var(--mono)" }} data-label={t("trips.weighing.col.ticket", "Ticket no.")}>
              {w.ticketNo ?? "—"}
            </td>
            <td style={td} data-label={t("trips.weighing.col.time", "Time")}>
              {new Date(w.measuredAt).toLocaleString(i18n.resolvedLanguage)}
            </td>
            <td style={td} data-label={t("trips.weighing.col.reason", "Reason")}>
              {w.kind === "CONTROL" ? (w.reason ?? "—") : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Same read-only field pattern as `TripDetailPage`'s own `ReadField` — `mono`
 * for a plate (a code), unset for a name (free text). */
function ReadField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={fieldLabel}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ink)", fontFamily: mono ? "var(--mono)" : undefined }}>
        {value}
      </span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const idGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
  gap: 12,
};
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const sectionTitle: CSSProperties = { margin: 0, fontSize: 13, fontWeight: 600, color: "var(--ink-2)" };
const th: CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: "var(--ink-3)" };
const td: CSSProperties = { padding: "8px 10px", fontSize: 13 };
const divider: CSSProperties = { borderTop: "1px solid var(--border)" };
const errBox: CSSProperties = {
  padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13,
};
